import { useQuery, useMutation } from "@tanstack/react-query";
import { metaApi } from "@/api";
import type { MetaConversionLog } from "@/types";
import { META_EVENT_STATUS_LABELS } from "@/types";
import { ArrowLeft, CheckCircle, XCircle, Wifi } from "lucide-react";
import { useNavigate } from "react-router";

const eventStatusColors: Record<string, string> = {
  PENDING: "bg-yellow-100 text-yellow-700",
  SENT: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
};

export default function MetaSettings() {
  const navigate = useNavigate();

  const { data: settings, isLoading } = useQuery({
    queryKey: ["meta-settings"],
    queryFn: () => metaApi.settings(),
  });

  const { data: logsData } = useQuery({
    queryKey: ["meta-capi-logs"],
    queryFn: () => metaApi.capiLogs({ limit: "20" }),
  });

  const testMutation = useMutation({
    mutationFn: () => metaApi.testConnection(),
  });

  const logs = logsData?.data ?? [];

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate("/meta-audiences")}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
      >
        <ArrowLeft size={16} />
        オーディエンス一覧に戻る
      </button>

      <h1 className="text-2xl font-bold mb-6">Meta広告連携 設定</h1>

      {isLoading ? (
        <div className="text-center py-12 text-slate-500">読み込み中...</div>
      ) : (
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h2 className="text-sm font-bold mb-4">接続状態</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {settings?.configured ? (
                    <CheckCircle size={16} className="text-green-500" />
                  ) : (
                    <XCircle size={16} className="text-red-400" />
                  )}
                  <span className="text-sm">Custom Audiences</span>
                </div>
                <span className="text-xs text-slate-500">
                  {settings?.adAccountId || "未設定"}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {settings?.capiConfigured ? (
                    <CheckCircle size={16} className="text-green-500" />
                  ) : (
                    <XCircle size={16} className="text-red-400" />
                  )}
                  <span className="text-sm">Conversions API</span>
                </div>
                <span className="text-xs text-slate-500">
                  {settings?.pixelId || "未設定"}
                </span>
              </div>
              {settings?.testEventCode && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-orange-600">テストモード有効</span>
                  <span className="text-xs text-slate-500">{settings.testEventCode}</span>
                </div>
              )}
            </div>
            <div className="mt-4 pt-3 border-t">
              <button
                onClick={() => testMutation.mutate()}
                disabled={testMutation.isPending || !settings?.configured}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500 text-white rounded-md text-sm hover:bg-blue-600 disabled:opacity-50"
              >
                <Wifi size={14} />
                {testMutation.isPending ? "テスト中..." : "接続テスト"}
              </button>
              {testMutation.isSuccess && testMutation.data && (
                <div className="mt-3 p-3 bg-green-50 rounded text-sm">
                  {testMutation.data.adAccount && (
                    <div>広告アカウント: {testMutation.data.adAccount.name} ({testMutation.data.adAccount.id})</div>
                  )}
                  {testMutation.data.pixel && (
                    <div>ピクセル: {testMutation.data.pixel.name} ({testMutation.data.pixel.id})</div>
                  )}
                </div>
              )}
              {testMutation.isError && (
                <div className="mt-3 p-3 bg-red-50 rounded text-sm text-red-600">
                  {(testMutation.error as Error).message}
                </div>
              )}
            </div>
          </div>

          {/* Environment Note */}
          <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
            <p className="text-xs text-slate-500">
              認証情報（META_ACCESS_TOKEN, META_AD_ACCOUNT_ID, META_PIXEL_ID）は環境変数で管理されています。
              変更する場合はサーバー側の .env ファイルを編集してください。
            </p>
          </div>

          {/* CAPI Logs */}
          <div className="bg-white p-4 rounded-lg shadow-sm border">
            <h2 className="text-sm font-bold mb-4">Conversions API ログ（直近20件）</h2>
            {logs.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">送信ログはまだありません</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">日時</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">イベント</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">イベントID</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">ステータス</th>
                      <th className="text-left text-xs font-medium text-slate-500 px-3 py-2">エラー</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log: MetaConversionLog) => (
                      <tr key={log.id} className="border-b">
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {new Date(log.createdAt).toLocaleString("ja-JP")}
                        </td>
                        <td className="px-3 py-2 text-xs">{log.eventName}</td>
                        <td className="px-3 py-2 text-xs text-slate-500 font-mono truncate max-w-[200px]">
                          {log.eventId}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${eventStatusColors[log.status]}`}>
                            {META_EVENT_STATUS_LABELS[log.status]}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-red-500 truncate max-w-[200px]">
                          {log.errorMessage || "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
